/**
 * Montos del contrato vigente.
 *
 * Estos números estaban escritos dentro del código del Resumen, así que el
 * semáforo anunciaba un contrato excedido incluso con la base vacía. Ahora se
 * administran acá y el Resumen los lee de la base.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Check, FileSignature, Loader2 } from 'lucide-react';
import { Boton, Campo, Chip, Tarjeta, TarjetaTitulo } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { CONTRATO_INICIAL, guardarContrato, leerContrato } from '@/db/contrato';
import { controlarContrato } from '@/domain/precios';
import { uf as fuf, clp } from '@/domain/formato';

/** Acepta «394.025,5» y «394025.5» por igual. */
function aNumero(s: string): number {
  const limpio = s.trim().replace(/\./g, '').replace(',', '.');
  const n = Number(limpio);
  return Number.isFinite(n) ? n : 0;
}

function aTexto(n: number): string {
  return n.toLocaleString('es-CL', { maximumFractionDigits: 3 });
}

export default function Contrato() {
  const { perfil } = useAuth();

  const [numero, setNumero] = useState('');
  const [base, setBase] = useState('');
  const [modificaciones, setModificaciones] = useState('');
  const [cobrado, setCobrado] = useState('');
  const [valorUf, setValorUf] = useState('');

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [configurado, setConfigurado] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const c = (await leerContrato()) ?? null;
        const d = c ?? CONTRATO_INICIAL;
        setConfigurado(Boolean(c));
        setNumero(d.numero);
        setBase(aTexto(d.montoBaseUf));
        setModificaciones(aTexto(d.montoModificacionesUf));
        setCobrado(aTexto(d.cobradoAcumuladoUf));
        setValorUf(aTexto(d.valorUf));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo leer el contrato.');
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  const datos = {
    numero: numero.trim(),
    montoBaseUf: aNumero(base),
    montoModificacionesUf: aNumero(modificaciones),
    cobradoAcumuladoUf: aNumero(cobrado),
    valorUf: aNumero(valorUf),
  };
  const vista = controlarContrato(datos);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    setAviso(null);
    try {
      await guardarContrato(datos, perfil?.uid ?? null);
      setConfigurado(true);
      setAviso('Contrato guardado. El semáforo del Resumen ya usa estos montos.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el contrato.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Tarjeta>
      <TarjetaTitulo
        accion={
          <Chip nivel={configurado ? 'VERDE' : 'AMARILLO'}>
            {configurado ? 'Configurado' : 'Sin configurar'}
          </Chip>
        }
      >
        Contrato vigente
      </TarjetaTitulo>

      {cargando ? (
        <div className="flex items-center gap-2 p-4 text-xs text-texto-mute">
          <Loader2 size={14} className="animate-spin" />
          Leyendo el contrato…
        </div>
      ) : (
        <form onSubmit={(e) => void enviar(e)} className="space-y-3 p-4">
          {error && (
            <p className="rounded-control border border-fluor-rojo/40 bg-fluor-rojo/10 px-3 py-2 text-xs text-fluor-rojo">
              {error}
            </p>
          )}
          {aviso && (
            <p className="rounded-control border border-fluor-verde/40 bg-fluor-verde/10 px-3 py-2 text-xs text-fluor-verde">
              {aviso}
            </p>
          )}

          <Campo
            etiqueta="Número de contrato"
            name="contrato-numero"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="GD KM 13 NV 17"
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etiqueta="Monto base"
              name="contrato-base"
              value={base}
              onChange={(e) => setBase(e.target.value)}
              sufijo="UF"
              inputMode="decimal"
            />
            <Campo
              etiqueta="Modificaciones aprobadas"
              name="contrato-modificaciones"
              value={modificaciones}
              onChange={(e) => setModificaciones(e.target.value)}
              sufijo="UF"
              inputMode="decimal"
            />
            <Campo
              etiqueta="Cobrado acumulado"
              name="contrato-cobrado"
              value={cobrado}
              onChange={(e) => setCobrado(e.target.value)}
              sufijo="UF"
              inputMode="decimal"
            />
            <Campo
              etiqueta="Valor de la UF"
              name="contrato-valoruf"
              value={valorUf}
              onChange={(e) => setValorUf(e.target.value)}
              sufijo="$"
              inputMode="decimal"
            />
          </div>

          <p className="rounded-control border border-linea bg-petroleo-800/40 px-3 py-2 text-[11px] leading-relaxed text-texto-mute">
            El <strong>cobrado acumulado</strong> es lo facturado antes de esta app — los estados de
            pago que llevaba la planilla. Se ingresa a mano y no se calcula con las guías cargadas.
            Cuando la app emita sus propios estados de pago, habrá que sumarlos a esta base.
          </p>

          <div className="rounded-control border border-linea bg-petroleo-800/30 px-3 py-2 text-xs">
            <div className="text-texto-mute">Con estos montos, el Resumen mostraría:</div>
            <div className="mt-1 font-medium">{vista.mensaje}</div>
            <div className="mt-0.5 text-[11px] text-texto-mute">
              Contratado UF {fuf(vista.montoVigenteTotalUf, 0)} · saldo UF {fuf(vista.saldoUf, 0)}
              {vista.excedido && ` · exceso ${clp(vista.excesoClp)}`}
            </div>
          </div>

          <Boton type="submit" variante="primario" icono={<Check size={15} />} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar contrato'}
          </Boton>
        </form>
      )}

      <p className="flex items-start gap-2 px-4 pb-4 text-[11px] text-texto-mute">
        <FileSignature size={13} className="mt-0.5 shrink-0" />
        Mientras no esté configurado, el Resumen no muestra semáforo de contrato en vez de suponer
        montos.
      </p>
    </Tarjeta>
  );
}
